import React, { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import type { UseFloatingOptions } from "@floating-ui/react";
import "react-datepicker/dist/react-datepicker.css";
import "./customComponentStyles/DateRangeSelector.css";
import { CalendarIcon } from "./ProjectIcons";
import {
  formatDateAsLocalString,
  preSelectionFromTypedPrefix,
  preSelectionFromTypedYearMonth,
} from "./helpers";

type SingleDatePickerProps = {
  selected: Date | null;
  onChange: (date: Date | null) => void;
  placeholder: string;
  ariaLabel: string;
  /** Days before this are disabled. */
  minDate?: Date;
  /** Days after this are disabled. */
  maxDate?: Date;
  calendarClassName: string;
  inputClassName: string;
  todayButtonLabel: string;
  /** Extra keydown handler chained after react-datepicker's own injected
   * one and this component's Enter-triggered year/month snap — e.g. a
   * caller that wants Enter in this field to also submit a parent search. */
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Shows a calendar glyph in the field while empty (a "pick a date"
   * affordance); once a date is chosen, `isClearable`'s own "x" takes that
   * spot instead. Off by default since not every caller wants it. */
  showCalendarIcon?: boolean;
  /**
   * When set, forces the calendar to always open at this placement instead
   * of react-datepicker's default flip/shift behavior (which reconsiders
   * placement — including flipping between above/below — as content like
   * the visible month changes size). Needed when the field can sit near an
   * edge with inconsistent room (e.g. inside a scrollable modal); toolbar
   * placements with reliable open space below can omit this and get the
   * normal flip-to-fit behavior.
   */
  fixedPopperPlacement?: "top-start" | "bottom-start";
  /**
   * Called with the field's displayed text whenever it changes, including
   * the resync after a commit. The displayed text can differ from
   * `selected`: a typed date react-datepicker refuses (outside
   * `minDate`/`maxDate`, impossible, or incomplete) stays on screen
   * uncommitted. A caller that must not act on a value the user cannot see
   * compares the two (AKL-1007).
   */
  onDraftTextChange?: (text: string) => void;
};

/**
 * One independent date field — the building block both the review filter
 * modal's date-range control and the shared toolbar `DateRangeSelector` are
 * built from, so a start/end pair can never drift in behavior. Typeable
 * (react-datepicker's own type-to-select), with month/year dropdowns and a
 * live-typing enhancement: the open calendar snaps to a partially-typed
 * year/month before the date is complete (see
 * `preSelectionFromTypedPrefix`/`preSelectionFromTypedYearMonth` in
 * `helpers.tsx`). `strictParsing` is required — without it, react-datepicker
 * falls back to the native `new Date(value)` constructor whenever its
 * format-based parse fails, and native parsing of a bare partial string
 * like "1" doesn't fail, it silently resolves to a real (nonsensical) date
 * (2001-01-01 in V8) that then gets committed via onChange mid-typing.
 * The popover always positions with strategy "fixed" (relative to the
 * viewport, not the nearest scrollable ancestor) so it can't get clipped
 * by a container that's currently shorter than the calendar — e.g. a
 * table that hasn't grown to its loaded height yet.
 *
 * The input's displayed text is a local "draft" (`draftText`), not
 * react-datepicker's own internal value — react-datepicker unconditionally
 * resets its internal text to blank/the last committed value whenever the
 * calendar closes (`setOpen(false)` always does `setState({inputValue:
 * null})`, e.g. on Escape, on Tab, and on Enter over a still-partial date,
 * since `strictParsing` correctly refuses to commit it). That reset would
 * wipe out a still-in-progress partial date the moment the reviewer hits
 * Enter or clicks away, which is the opposite of what they'd want. Since
 * `draftText` only resyncs from `selected` when the selected day actually
 * changes (a real commit, an external reset, a cleared filter — never a
 * no-op partial edit, and never a new Date object for the same day), it
 * survives react-datepicker's internal resets.
 */
const SingleDatePicker: React.FC<SingleDatePickerProps> = ({
  selected,
  onChange,
  placeholder,
  ariaLabel,
  minDate,
  maxDate,
  calendarClassName,
  inputClassName,
  todayButtonLabel,
  showCalendarIcon = false,
  fixedPopperPlacement,
  onKeyDown,
  onDraftTextChange,
}) => {
  const datePickerRef = useRef<DatePicker>(null);
  const [draftText, setDraftText] = useState(formatDateAsLocalString(selected));

  // Resync on the date's value, not its identity: a caller that builds a new
  // Date for the same day on each render (ReviewDateRangeInput does) would
  // otherwise reset the text on every parent render and undo whatever is
  // being typed (Copilot, PR #821).
  const selectedText = formatDateAsLocalString(selected);
  useEffect(() => {
    setDraftText(selectedText);
  }, [selectedText]);

  useEffect(() => {
    onDraftTextChange?.(draftText);
  }, [draftText, onDraftTextChange]);

  // Live-typing feedback: as soon as a complete year or year+month is typed,
  // navigate the open calendar there without waiting for a full date.
  const handleChangeRaw = (
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
  ) => {
    const value = (event?.target as HTMLInputElement | undefined)?.value;
    if (value === undefined) return;
    const preSelection = preSelectionFromTypedPrefix(value);
    if (preSelection) datePickerRef.current?.setPreSelection(preSelection);
  };

  // draftText/onKeyDown/inputClassName change across renders (draftText on
  // every keystroke), but CustomInput's own identity must not — recreating
  // the forwardRef component on every render would make React treat it as
  // a brand new element type and remount the underlying <input>, stealing
  // focus after every keystroke. useMemo(..., []) keeps one stable
  // component for this instance's lifetime; it reads the values that
  // change via refs (kept in sync below) instead of closing over them
  // directly, so it still always renders the current draft text.
  const draftTextRef = useRef(draftText);
  draftTextRef.current = draftText;
  const onKeyDownRef = useRef(onKeyDown);
  onKeyDownRef.current = onKeyDown;
  const inputClassNameRef = useRef(inputClassName);
  inputClassNameRef.current = inputClassName;

  // `onKeyDown`/`onChange` are chained (not replaced): react-datepicker's
  // own injected handlers (parsing on change; Enter/Escape/Tab on keydown)
  // still need to run. This must live on the input, not as a top-level
  // `<DatePicker onKeyDown>` prop — that slot is a global observer called
  // from several internal handlers (the input's own, each day cell's,
  // etc.), so overriding it with a handler that only reacted to one key
  // broke the rest of the calendar's normal keyboard interaction while
  // typing. `value` is NOT forwarded from the injected props — see this
  // component's docstring for why the input is drafted locally instead.
  const CustomInput = useMemo(
    () =>
      React.forwardRef<
        HTMLInputElement,
        React.InputHTMLAttributes<HTMLInputElement>
      >(
        (
          {
            onKeyDown: injectedOnKeyDown,
            onChange: injectedOnChange,
            value: _injectedValue,
            ...rest
          },
          ref,
        ) => (
          <input
            ref={ref}
            {...rest}
            value={draftTextRef.current}
            className={inputClassNameRef.current}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setDraftText(event.target.value);
              injectedOnChange?.(event);
            }}
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === "Enter") {
                const preSelection = preSelectionFromTypedYearMonth(
                  draftTextRef.current,
                );
                if (preSelection)
                  datePickerRef.current?.setPreSelection(preSelection);
              }
              injectedOnKeyDown?.(event);
              onKeyDownRef.current?.(event);
            }}
          />
        ),
      ),
    [],
  );

  return (
    <div className="relative flex-1 min-w-0">
      <DatePicker
        ref={datePickerRef}
        selected={selected}
        onChange={onChange}
        onChangeRaw={handleChangeRaw}
        minDate={minDate}
        maxDate={maxDate}
        dateFormat="yyyy-MM-dd"
        strictParsing
        placeholderText={placeholder}
        isClearable
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        todayButton={todayButtonLabel}
        calendarClassName={calendarClassName}
        wrapperClassName="block w-full"
        {...(fixedPopperPlacement
          ? { popperPlacement: fixedPopperPlacement }
          : {})}
        popperProps={
          // react-datepicker's PopperProps type declares this as
          // `Omit<UseFloatingOptions, "middleware">`, but the runtime
          // shallow-merges popperProps directly into the floating-ui
          // options object, so a `middleware` key here does take effect
          // despite the type omitting it. `strategy: "fixed"` is always
          // set — the default "absolute" strategy positions the popover
          // relative to its nearest scrollable/positioned ancestor, so an
          // ancestor shorter than the calendar (e.g. a table container
          // that hasn't grown to its loaded height yet) clips it; "fixed"
          // positions relative to the viewport instead, so it can't be
          // clipped that way. `middleware: []` is added on top only when
          // `fixedPopperPlacement` is requested — that's the only way to
          // fully replace, rather than merely append to, react-datepicker's
          // hardcoded flip/offset/arrow middleware (which otherwise keeps
          // reconsidering placement, e.g. flipping above/below as the
          // visible month's height changes). Cast via `unknown` (not
          // `any`) so this stays a narrow, explicit escape hatch.
          {
            strategy: "fixed",
            ...(fixedPopperPlacement ? { middleware: [] } : {}),
          } as unknown as Omit<UseFloatingOptions, "middleware">
        }
        dayClassName={(date) =>
          selected && date.getTime() === selected.getTime()
            ? "selected-day"
            : ""
        }
        customInput={<CustomInput aria-label={ariaLabel} />}
      />
      {showCalendarIcon && !selected && (
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
          <CalendarIcon />
        </span>
      )}
    </div>
  );
};

export default SingleDatePicker;
